import React from 'react'
import { SnackbarContent, type CustomContentProps, useSnackbar, type OptionsObject, type SnackbarMessage, type VariantType, enqueueSnackbar, closeSnackbar as notiCloseSnackbar, type SnackbarKey } from 'notistack'
import { Alert, type AlertColor, AlertTitle, Typography, IconButton, CircularProgress } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useTheme } from '@mui/material/styles'
import { ReactComponent as CheckCircleOutlineIcon } from '../../assets/check-with-circle.svg'
import CloseIcon from '@mui/icons-material/Close'
import { translateUserFacingText } from '../../utils/userFacingTerminology'
import { yellow } from '../../colors'

declare module 'notistack' {
  interface VariantOverrides {
    loading: true
  }
}

interface NotificationsProps extends CustomContentProps {
  subTitle?: React.ReactNode
}

export const Notifications = React.forwardRef<HTMLDivElement, NotificationsProps>((props, ref) => {
  const {
    id,
    message,
    subTitle,
    variant,
    ...other
  } = props

  const theme = useTheme()

  const { closeSnackbar } = useSnackbar()

  const iconMapping = {
    success: <CheckCircleOutlineIcon />,
    warning: <WarningAmberIcon />,
    error: <InfoOutlinedIcon sx={{ rotate: '180deg' }}/>
  }

  // Match product warning yellow (CellWarning / UploadModalAlert), not MUI's default orange.
  const warningFilledSx = variant === 'warning'
    ? {
        backgroundColor: yellow[150],
        color: '#fff',
        '.MuiAlert-icon': { color: '#fff' }
      }
    : {}

  return (
    <SnackbarContent ref={ref} role="alert" {...other}>
      <Alert
        iconMapping={iconMapping}
        severity={variant as AlertColor}
        variant="filled"
         onClose={() => { closeSnackbar(id) }}
         action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            sx={{ position: 'absolute', right: theme.spacing(2), top: theme.spacing(3) }}
            onClick={() => { closeSnackbar(id) }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{
          width: theme.spacing(100),
          padding: theme.spacing(4),
          borderRadius: theme.spacing(3),
          ...warningFilledSx
        }} >
        <AlertTitle sx={{ fontSize: '14px', fontWeight: '600' }}>{message}</AlertTitle>
        {typeof subTitle === 'string' || typeof subTitle === 'number'
          ? <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-line' }}>{subTitle}</Typography>
          : subTitle}
      </Alert>

    </SnackbarContent>
  )
})

Notifications.displayName = 'Notifications'

export const LoadingNotifications = React.forwardRef<HTMLDivElement, CustomContentProps>((props, ref) => {
  const { id, message, ...other } = props
  const theme = useTheme()
  const { closeSnackbar } = useSnackbar()

  return (
    <SnackbarContent ref={ref} role="status" {...other}>
      <Alert
        icon={<CircularProgress size={20} color="inherit" />}
        severity="info"
        variant="filled"
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            sx={{ position: 'absolute', right: theme.spacing(2), top: theme.spacing(3) }}
            onClick={() => { closeSnackbar(id) }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{ width: theme.spacing(100), padding: theme.spacing(4), borderRadius: theme.spacing(3) }}
      >
        <AlertTitle sx={{ fontSize: '14px', fontWeight: '600' }}>{message}</AlertTitle>
      </Alert>
    </SnackbarContent>
  )
})

LoadingNotifications.displayName = 'LoadingNotifications'

interface ShowNotificationOptions<V extends VariantType> extends OptionsObject<V> {
  subTitle?: React.ReactNode
}

const translateSnackbarMessage = (message: SnackbarMessage): SnackbarMessage => {
  if (typeof message !== 'string') return message

  return translateUserFacingText(message)
}

const translateSubTitle = (subTitle: React.ReactNode): React.ReactNode => {
  if (typeof subTitle !== 'string') return subTitle

  return translateUserFacingText(subTitle)
}

export const notification = (message: SnackbarMessage, options: ShowNotificationOptions<VariantType>): SnackbarKey => {
  const translatedOptions: ShowNotificationOptions<VariantType> = {
    ...options,
    subTitle: translateSubTitle(options.subTitle)
  }

  return enqueueSnackbar(translateSnackbarMessage(message), translatedOptions)
}

export const closeNotification = (key?: SnackbarKey): void => {
  notiCloseSnackbar(key)
}

const FLASH_STORAGE_KEY = 'flashNotifications'

interface FlashNotification {
  message: string
  variant?: VariantType
  subTitle?: string
}

/**
 * Queue a notification to be shown after the next full page reload.
 *
 * Some flows call `window.location.reload()` right after an action (e.g. a user
 * editing their own roles). A normally-enqueued snackbar is torn down by that
 * reload before it can be read. `flashNotification` persists the message in
 * `sessionStorage` so `flushFlashNotifications` can re-show it once the app
 * remounts. Only use this when a reload is imminent — otherwise the message
 * would sit unshown until the next load. Flushed messages are shown with
 * `persist` so they stay until the user dismisses them.
 */
export const flashNotification = (
  message: string,
  options: { variant?: VariantType, subTitle?: string } = {}
): void => {
  try {
    const raw = sessionStorage.getItem(FLASH_STORAGE_KEY)
    const queue: FlashNotification[] = raw != null ? JSON.parse(raw) : []
    queue.push({ message, variant: options.variant, subTitle: options.subTitle })
    sessionStorage.setItem(FLASH_STORAGE_KEY, JSON.stringify(queue))
  } catch {
    // sessionStorage unavailable (private mode, etc.) — show it now instead so
    // the message is not lost entirely.
    notification(message, { ...options, persist: true })
  }
}

/**
 * Drain and display any notifications queued via `flashNotification` before the
 * last reload. Call once on app mount (inside the SnackbarProvider).
 */
export const flushFlashNotifications = (): void => {
  try {
    const raw = sessionStorage.getItem(FLASH_STORAGE_KEY)
    if (raw == null) return
    sessionStorage.removeItem(FLASH_STORAGE_KEY)
    const queue: FlashNotification[] = JSON.parse(raw)
    queue.forEach(({ message, variant, subTitle }) => {
      notification(message, { variant, subTitle, persist: true })
    })
  } catch {
    // Malformed queue — clear it so it cannot wedge future loads.
    try { sessionStorage.removeItem(FLASH_STORAGE_KEY) } catch {}
  }
}
